const FADE_DELAY_MS = 800;
const FADE_OUT_MS = 250;
const THUMB_WIDTH = 5;
const MIN_THUMB_HEIGHT = 24;
const EDGE_INSET = 2;

// Containers that render their own overlay thumb (ui/scroll-area.jsx, the
// in-game logbook) or explicitly opt out are skipped so they don't get a
// second thumb on top of their own.
const SKIP_SELECTOR = '.scroll-area-viewport, .logbook-scroll, [data-overlay-scrollbar="off"]';

/**
 * App-wide overlay scrollbars.
 *
 * Native scrollbars are fully hidden by global CSS (they always consume
 * layout width in Chromium, shifting content the moment an element becomes
 * scrollable). This module listens for scroll events in the capture phase
 * and floats a fixed-position thumb (styled by `.overlay-scrollbar-thumb`
 * in index.css) over whichever element is scrolling. No per-component
 * wiring needed. The thumb fades out shortly after scrolling stops and
 * supports drag-to-scroll while visible.
 *
 * For scroll regions that should show a persistent thumb whenever they
 * overflow (dropdown menus, pickers), use the ScrollArea component from
 * ui/scroll-area.jsx instead.
 */
export function installOverlayScrollbars() {
  if (typeof document === 'undefined') return;

  const active = new Map();

  function remove(el) {
    const entry = active.get(el);
    if (!entry) return;
    clearTimeout(entry.hideTimer);
    clearTimeout(entry.removeTimer);
    entry.node.remove();
    active.delete(el);
  }

  function ensureEntry(el) {
    let entry = active.get(el);
    if (!entry) {
      const node = document.createElement('div');
      node.className = 'overlay-scrollbar-thumb';
      document.body.appendChild(node);
      entry = { node, hideTimer: 0, removeTimer: 0, dragging: false, hovered: false };
      wireThumb(el, entry);
      active.set(el, entry);
    }
    clearTimeout(entry.removeTimer);
    entry.node.style.opacity = '1';
    return entry;
  }

  function position(el, entry) {
    const { scrollTop, scrollHeight, clientHeight } = el;
    if (!el.isConnected || scrollHeight <= clientHeight + 1) {
      remove(el);
      return false;
    }
    const rect = el.getBoundingClientRect();
    const height = Math.max(MIN_THUMB_HEIGHT, (clientHeight / scrollHeight) * rect.height);
    const maxTop = rect.height - height;
    const top = rect.top + maxTop * (scrollTop / (scrollHeight - clientHeight));
    entry.node.style.height = `${height}px`;
    entry.node.style.top = `${top}px`;
    entry.node.style.left = `${rect.right - THUMB_WIDTH - EDGE_INSET}px`;
    return true;
  }

  function scheduleHide(el, entry) {
    clearTimeout(entry.hideTimer);
    entry.hideTimer = setTimeout(() => {
      if (entry.dragging || entry.hovered) {
        scheduleHide(el, entry);
        return;
      }
      entry.node.style.opacity = '0';
      entry.removeTimer = setTimeout(() => remove(el), FADE_OUT_MS);
    }, FADE_DELAY_MS);
  }

  function wireThumb(el, entry) {
    const { node } = entry;
    node.addEventListener('pointerenter', () => {
      entry.hovered = true;
      clearTimeout(entry.removeTimer);
      node.style.opacity = '1';
    });
    node.addEventListener('pointerleave', () => {
      entry.hovered = false;
      scheduleHide(el, entry);
    });
    node.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopPropagation();
      entry.dragging = true;
      const startY = event.clientY;
      const startScrollTop = el.scrollTop;

      function handleMove(moveEvent) {
        const trackRange = el.getBoundingClientRect().height - node.offsetHeight;
        if (trackRange <= 0) return;
        const scrollRange = el.scrollHeight - el.clientHeight;
        el.scrollTop = startScrollTop + ((moveEvent.clientY - startY) / trackRange) * scrollRange;
      }
      function handleUp() {
        entry.dragging = false;
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        scheduleHide(el, entry);
      }
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
    });
  }

  document.addEventListener('scroll', (event) => {
    const el = event.target === document ? document.documentElement : event.target;
    if (!(el instanceof HTMLElement) || el.matches(SKIP_SELECTOR)) return;
    const entry = ensureEntry(el);
    if (position(el, entry)) scheduleHide(el, entry);
  }, true);
}
